(() => {
  const P = () => window.GhaliaPricing;
  const moneyNet = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);
  const moneyDisplay = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);

  const isFreeGiftLine = (item) => {
    const props = item?.properties;
    if (!props) return false;
    if (typeof props === 'object' && !Array.isArray(props)) {
      return props._ghalia_free_gift === 'true';
    }
    if (Array.isArray(props)) {
      return props.some((entry) => entry?.name === '_ghalia_free_gift' && entry?.value === 'true');
    }
    return false;
  };
  const isBundleLine = (item) => Boolean(item?.properties?._ghalia_stack_anchor);

  const NATIVE_BUNDLE_HANDLES =
    window.GhaliaNativeBundleHandles ||
    new Set([
      'ghalia-infinity-ring-duo',
      'knot-bridge-duo-silver',
      'knot-bridge-duo-gold',
      'ghalia-knot-hero-set-gold',
      'ghalia-knot-hero-set-silver',
      'ghalia-knot-two-tone-ring-stack',
      'ghalia-knot-wrist-story',
      'ghalia-pave-gold-stack',
      'ghalia-pave-silver-stack',
      'ghalia-cross-ring-earring-gold',
    ]);

  const isNativeBundleLine = (item) => NATIVE_BUNDLE_HANDLES.has(item?.handle);

  const nativeBundleDiscountPct = (handle) => {
    const map = window.GhaliaNativeBundleDiscounts || {};
    const pct = Number(map[handle]);
    return pct > 0 ? pct : 0;
  };

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const storedPromoCode = () => {
    try {
      return sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const cartPromoApp = (cart) =>
    (cart?.cart_level_discount_applications || []).find((d) => d?.type === 'discount_code');

  const isPromoCodeApplication = (app) =>
    window.GhaliaPromoConfig?.isDiscountCodeApplication?.(app) ?? app?.type === 'discount_code';

  const hasPromoCode = (cart) => {
    if (cartPromoApp(cart)) return true;
    return (cart?.items || []).some(
      (item) =>
        !isNativeBundleLine(item) &&
        (item.line_level_discount_allocations || []).some((alloc) =>
          isPromoCodeApplication(alloc?.discount_application)
        )
    );
  };

  const lineOriginalNet = (item, row) => {
    const qty = Number(item.quantity) || 1;
    const origLine = Number(item.original_line_price) || Number(item.line_price) || 0;
    if (origLine > 0) return origLine;
    const domNet = Number(row?.dataset?.cartLineNet || 0);
    const domQty = Number(row?.querySelector('[data-cart-line-qty]')?.value) || qty;
    if (domNet > 0 && domQty > 0) return Math.round(domNet * (qty / domQty));
    if (domNet > 0) return domNet;
    const unit = Number(item.original_price ?? item.price ?? 0);
    if (unit > 0) return unit * qty;
    return Number(item.line_price) || 0;
  };

  const lineFinalNet = (item) =>
    Number(item.final_line_price) || Number(item.line_price) || lineOriginalNet(item);

  const syncLineCustomerAmounts = (row, pricing, netCents) => {
    if (!row || !pricing?.displayCents) return;
    const net = Number(netCents) || 0;
    if (net <= 0) return;
    row.dataset.cartLineCustomerNet = String(net);
    row.dataset.cartLineDisplay = String(pricing.displayCents(net));
  };

  const syncLineCompareDisplay = (row, pricing, netCents) => {
    if (!row || !pricing?.displayCents) return;
    const net = Number(netCents) || 0;
    if (net <= 0) return;
    row.dataset.cartLineCompareDisplay = String(pricing.displayCents(net));
  };

  const setCustomerLinePrice = (currentEl, pricing, netCents, row) => {
    if (!currentEl || !pricing) return;
    syncLineCustomerAmounts(row, pricing, netCents);
    const next = pricing.formatCustomerLineNet
      ? pricing.formatCustomerLineNet(netCents, row)
      : pricing.formatNet(netCents);
    if (currentEl.textContent.trim() !== next.trim()) currentEl.textContent = next;
  };

  const isCodePromoAllocation = (alloc) =>
    window.GhaliaPromoConfig?.isDiscountCodeApplication?.(alloc?.discount_application) ??
    alloc?.discount_application?.type === 'discount_code';

  const applyCodePromoLinePrice = (item, row, pricing, priceEl, compareEl, currentEl, cart, drawer) => {
    const promoCfg = window.GhaliaPromoConfig;
    if (cart && drawer && promoCfg?.shopifyCodePromoWithinCap?.(cart, drawer)) {
      const originalNet = lineOriginalNet(item, row);
      const promoAllocNet = promoCfg.lineCodePromoAllocNet?.(item) || 0;
      const finalNet =
        Number(item.final_line_price) ||
        (promoAllocNet > 0 ? Math.max(0, originalNet - promoAllocNet) : 0);
      if (originalNet > 0 && finalNet > 0 && finalNet < originalNet) {
        syncLineCustomerAmounts(row, pricing, finalNet);
        currentEl.textContent = pricing.formatCustomerLineNet(finalNet, row, { forceComputed: true });
        if (compareEl) {
          compareEl.textContent = pricing.formatNet(originalNet);
          compareEl.hidden = false;
          compareEl.removeAttribute('hidden');
          syncLineCompareDisplay(row, pricing, originalNet);
        }
        priceEl.classList.remove('is-stack-discounted');
        priceEl.classList.add('is-promo-discounted');
        return true;
      }
    }

    const hasCodePromo = (item.line_level_discount_allocations || []).some(isCodePromoAllocation);
    if (hasCodePromo) return false;

    const originalNet = lineOriginalNet(item, row);
    const finalNet = lineFinalNet(item);
    const allocNet = (item.line_level_discount_allocations || []).reduce(
      (sum, alloc) => sum + (Number(alloc.amount) || 0),
      0
    );
    const currentNet = allocNet > 0 ? Math.max(0, originalNet - allocNet) : finalNet;
    if (originalNet <= 0 || currentNet <= 0 || currentNet >= originalNet) return false;

    syncLineCustomerAmounts(row, pricing, currentNet);
    currentEl.textContent = pricing.formatCustomerLineNet(currentNet, row, { forceComputed: true });
    if (compareEl) {
      compareEl.textContent = pricing.formatNet(originalNet);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
      syncLineCompareDisplay(row, pricing, originalNet);
    }
    priceEl.classList.remove('is-stack-discounted');
    priceEl.classList.add('is-promo-discounted');
    return true;
  };

  const cartHasNativeBundle = (cart) =>
    (cart?.items || []).some((item) => isNativeBundleLine(item));

  const parseStacksFromCart = (cart) => {
    const stacks = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      const anchor = item.properties?._ghalia_stack_anchor;
      const pct = parseInt(item.properties?._ghalia_stack_pct, 10) || 0;
      const membersRaw = item.properties?._ghalia_stack_members || '';
      if (!anchor || !pct || !membersRaw) return;
      const members = membersRaw.split(',').map((h) => h.trim()).filter(Boolean);
      if (!members.length) return;
      if (!stacks.has(anchor)) stacks.set(anchor, { anchor, pct, members });
    });
    return [...stacks.values()];
  };

  const handlesInCart = (cart) => {
    const set = new Set();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      if (item.handle) set.add(item.handle);
    });
    return set;
  };

  const isStackComplete = (cart, stack) => {
    const present = handlesInCart(cart);
    return stack.members.every((handle) => present.has(handle));
  };

  const qtyByHandle = (cart) => {
    const map = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      if (!item.handle) return;
      map.set(item.handle, (map.get(item.handle) || 0) + (Number(item.quantity) || 1));
    });
    return map;
  };

  const buildBundleUnitsByLineKey = (cart, stacks) => {
    const perHandle = discountedQtyByHandle(cart, stacks);
    const remaining = new Map(perHandle);
    const byKey = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item) || !item.handle) return;
      const left = remaining.get(item.handle) || 0;
      const qty = Number(item.quantity) || 1;
      const setUnits = Math.min(qty, left);
      byKey.set(item.key, setUnits);
      remaining.set(item.handle, left - setUnits);
    });
    return byKey;
  };

  /** Bundle discount applies to min qty across set members (1 set = 1 unit each). */
  const discountedQtyByHandle = (cart, stacks) => {
    const qty = qtyByHandle(cart);
    const discounted = new Map();
    stacks.forEach((stack) => {
      if (!isStackComplete(cart, stack)) return;
      const setCount = Math.min(...stack.members.map((h) => qty.get(h) || 0));
      if (setCount <= 0) return;
      stack.members.forEach((h) => {
        discounted.set(h, Math.max(discounted.get(h) || 0, setCount));
      });
    });
    return discounted;
  };

  const scaleDomLineNet = (item, row) => {
    const qty = Number(item.quantity) || 1;
    const domNet = Number(row?.dataset?.cartLineNet || 0);
    if (domNet <= 0) return 0;
    const domQty = Number(row?.querySelector('[data-cart-line-qty]')?.value) || qty;
    if (domQty <= 0 || domQty === qty) return domNet;
    return Math.round(domNet * (qty / domQty));
  };

  const lineNetCents = (cart, drawer, item, pricing) => {
    const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
    const fromItem =
      Number(item.final_line_price) ||
      Number(item.line_price) ||
      Number(item.original_line_price) ||
      0;
    if (fromItem > 0) return fromItem;
    const fromDom = scaleDomLineNet(item, row);
    if (fromDom > 0) return fromDom;
    return pricing.cartLineAmounts(item, row).net;
  };

  const lineUnitNet = (item, row, pricing) => {
    const qty = Number(item.quantity) || 1;
    const { net, unitNet } = pricing.cartLineAmounts(item, row);
    if (unitNet > 0) return unitNet;
    return Math.round(net / qty);
  };

  const computeNetSubtotal = (cart, drawer, pricing) => {
    let netSubtotalCents = 0;
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      netSubtotalCents += lineNetCents(cart, drawer, item, pricing);
    });
    return netSubtotalCents;
  };

  const computeNativeBundleSavingsNet = (cart, drawer, pricing) => {
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (!isNativeBundleLine(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const finalNet = Number(item.final_line_price) || Number(item.line_price) || 0;
      let compareNet =
        Number(item.original_line_price) ||
        scaleDomLineNet(item, row) ||
        Number(item.line_price) ||
        0;
      if (compareNet <= finalNet && finalNet > 0) {
        const pct = nativeBundleDiscountPct(item.handle) || 8;
        if (pct > 0 && pct < 100) {
          compareNet = Math.round((finalNet * 100) / (100 - pct));
        }
      }
      if (compareNet > finalNet) sum += compareNet - finalNet;
    });
    return sum;
  };

  /** Ti Sento: read stack line properties, apply % when all members are in cart. */
  const computeStackState = (cart, drawer) => {
    const pricing = P();
    if (!pricing) return { qualified: false };

    const netSubtotalCents = computeNetSubtotal(cart, drawer, pricing);
    const stacks = parseStacksFromCart(cart).filter((stack) => isStackComplete(cart, stack));

    const state = {
      qualified: false,
      netSubtotalCents,
      savingsNetCents: 0,
      bundleSavingsNetCents: 0,
      pct: 0,
      memberSet: null,
      discountedQty: new Map(),
    };

    if (cartHasNativeBundle(cart)) {
      const bundleSavings = computeNativeBundleSavingsNet(cart, drawer, pricing);
      const nativePcts = (cart?.items || [])
        .filter((item) => isNativeBundleLine(item))
        .map((item) => nativeBundleDiscountPct(item.handle) || 8)
        .filter((pct) => pct > 0);
      state.pct = nativePcts.length ? Math.max(...nativePcts) : 8;
      state.bundleSavingsNetCents = bundleSavings;
      state.savingsNetCents = bundleSavings;
      state.qualified = bundleSavings > 0;
      return state;
    }

    if (!stacks.length) return state;

    const memberSet = new Set();
    stacks.forEach((stack) => stack.members.forEach((h) => memberSet.add(h)));
    const pct = Math.max(...stacks.map((s) => s.pct));
    const discountedQty = discountedQtyByHandle(cart, stacks);
    const bundleUnitsByLine =
      pricing.bundleUnitsByLineKey?.(cart) ?? buildBundleUnitsByLineKey(cart, stacks);

    let eligibleNetCents = 0;
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      const setUnits = bundleUnitsByLine.get(item.key) || 0;
      if (setUnits <= 0) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const unitNet = lineUnitNet(item, row, pricing);
      eligibleNetCents += unitNet * setUnits;
    });

    const themeSavingsNetCents = Math.round((eligibleNetCents * pct) / 100);

    state.memberSet = memberSet;
    state.discountedQty = discountedQty;
    state.bundleUnitsByLine = bundleUnitsByLine;
    state.pct = pct;
    state.qualified = themeSavingsNetCents > 0;
    state.bundleSavingsNetCents = themeSavingsNetCents;
    state.savingsNetCents = themeSavingsNetCents;

    return state;
  };

  const cartHasShopifyCodePromo = (cart) => {
    if (window.GhaliaPromoConfig?.cartDiscountCodeTitle?.(cart)) return true;
    return (cart?.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some(
        (alloc) => alloc?.discount_application?.type === 'discount_code'
      )
    );
  };

  const promoEligibleForCart = (cart, drawer) => {
    if (cartHasShopifyCodePromo(cart)) return true;
    const qualifyingNet = P()?.qualifyingSubtotalNet?.(cart, drawer) || 0;
    if (!window.GhaliaPromoConfig?.meetsMinimum?.(qualifyingNet, drawer)) return false;
    try {
      const stored = sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
      if (stored && !/^GHALIA CART GIFT/i.test(stored)) return true;
    } catch (_) { /* noop */ }
    return Boolean(window.GhaliaPromoConfig?.read?.(drawer)?.defaultCode);
  };

  const promoLineNetForItem = (item, row, cart, pricing) => {
    if (!pricing?.isPromoPaidLine?.(item)) return 0;
    const originalNet = lineOriginalNet(item, row);
    if (originalNet <= 0) return 0;
    const qty = Number(item.quantity) || 1;
    const unitsByLine = pricing.bundleUnitsByLineKey?.(cart) || new Map();
    const setUnits = unitsByLine.get(item.key) || 0;
    const promoUnits = Math.max(0, qty - setUnits);
    if (promoUnits <= 0) return 0;
    return Math.round((originalNet * promoUnits) / qty);
  };

  const applyPreviewPromoLinePrice = (item, row, pricing, priceEl, compareEl, currentEl, cart, drawer) => {
    if (!pricing?.isPromoPaidLine?.(item)) return false;
    if (applyCodePromoLinePrice(item, row, pricing, priceEl, compareEl, currentEl, cart, drawer)) {
      return true;
    }
    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    if (!window.GhaliaPromoConfig?.meetsMinimum?.(qualifyingNet, drawer)) return false;
    const promoSavings = window.GhaliaPromoConfig?.computeSavingsNet?.(qualifyingNet, drawer) || 0;
    if (promoSavings <= 0 || qualifyingNet <= 0) return false;

    const promoLineNet = promoLineNetForItem(item, row, cart, pricing);
    if (promoLineNet <= 0) return false;
    const lineShare = Math.round((promoSavings * promoLineNet) / qualifyingNet);
    if (lineShare <= 0) return false;
    const finalPromoNet = Math.max(0, promoLineNet - lineShare);
    const originalNet = lineOriginalNet(item, row);
    const currentNet =
      promoLineNet >= originalNet ? finalPromoNet : originalNet - promoLineNet + finalPromoNet;
    if (currentNet >= originalNet) return false;

    syncLineCustomerAmounts(row, pricing, currentNet);
    currentEl.textContent = pricing.formatCustomerLineNet(currentNet, row, { forceComputed: true });
    if (compareEl) {
      compareEl.textContent = pricing.formatNet(originalNet);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
      syncLineCompareDisplay(row, pricing, originalNet);
    }
    priceEl.classList.remove('is-stack-discounted');
    priceEl.classList.add('is-promo-discounted');
    return true;
  };

  const applyLinePrices = (cart, state, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return;

    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      if (!row) return;
      const priceEl = row.querySelector('[data-cart-line-price]');
      const compareEl = row.querySelector('[data-cart-line-compare]');
      const currentEl = row.querySelector('[data-cart-line-current]');
      if (!priceEl || !currentEl) return;

      if (isNativeBundleLine(item)) {
        const finalNet = Number(item.final_line_price) || lineFinalNet(item);
        const compareDisplayFromDom = Number(row?.dataset?.cartLineCompareDisplay || 0);
        let compareDisplay = 0;

        if (compareDisplayFromDom > pricing.displayCents(finalNet)) {
          compareDisplay = compareDisplayFromDom;
        } else if (Number(item.original_line_price) > finalNet) {
          compareDisplay = pricing.displayCents(Number(item.original_line_price));
        } else {
          const pct = nativeBundleDiscountPct(item.handle) || state.pct || 8;
          const compareNet = Math.round((finalNet * 100) / (100 - pct));
          compareDisplay = pricing.displayCents(compareNet);
        }

        setCustomerLinePrice(currentEl, pricing, finalNet, row);
        if (compareEl && compareDisplay > pricing.displayCents(finalNet)) {
          compareEl.textContent = pricing.formatDisplay(compareDisplay);
          compareEl.hidden = false;
          compareEl.removeAttribute('hidden');
          row.dataset.cartLineCompareDisplay = String(compareDisplay);
        } else if (compareEl) {
          compareEl.hidden = true;
        }
        priceEl.classList.remove('is-stack-discounted', 'is-promo-discounted');
        return;
      }

      const allocNet = (item.line_level_discount_allocations || []).reduce(
        (sum, alloc) => sum + (Number(alloc.amount) || 0),
        0
      );
      const originalNet = lineOriginalNet(item, row);
      const finalNet = lineFinalNet(item);
      const hasLinePromoPrice =
        allocNet > 0 || (originalNet > 0 && finalNet > 0 && finalNet < originalNet);
      if (hasLinePromoPrice && !isNativeBundleLine(item)) {
        if (applyCodePromoLinePrice(item, row, pricing, priceEl, compareEl, currentEl, cart, drawer)) {
          return;
        }
      }

      if (promoEligibleForCart(cart, drawer) && pricing.isPromoPaidLine?.(item)) {
        if (applyCodePromoLinePrice(item, row, pricing, priceEl, compareEl, currentEl, cart, drawer)) {
          return;
        }
        if (applyPreviewPromoLinePrice(item, row, pricing, priceEl, compareEl, currentEl, cart, drawer)) {
          return;
        }
        return;
      }

      const lineNet = lineFinalNet(item);
      const qty = Number(item.quantity) || 1;
      const unitNet = lineUnitNet(item, row, pricing);
      let currentNet = lineNet;
      let compareNet = 0;

      if (state.qualified && state.memberSet?.has(item.handle)) {
        const pct = state.pct || 0;
        const discountedUnits = state.bundleUnitsByLine?.get(item.key) || 0;
        const regularUnits = qty - discountedUnits;

        if (discountedUnits > 0) {
          const discountedUnitNet = pricing.applyDiscount(unitNet, pct);
          currentNet = unitNet * regularUnits + discountedUnitNet * discountedUnits;
          compareNet = unitNet * qty;
        }
      }

      setCustomerLinePrice(currentEl, pricing, currentNet, row);
      if (compareEl) {
        if (compareNet > currentNet) {
          compareEl.textContent = pricing.formatCustomerLineNet(compareNet, row, { forceComputed: true });
          syncLineCompareDisplay(row, pricing, compareNet);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
      priceEl.classList.remove('is-promo-discounted');
      priceEl.classList.toggle('is-stack-discounted', Boolean(compareNet > currentNet));
    });
  };

  const shippingNetCents = (drawer) => {
    const summary = drawer?.querySelector('[data-cart-summary]');
    if (summary && summary.dataset.shippingNetCents != null && summary.dataset.shippingNetCents !== '') {
      return Number(summary.dataset.shippingNetCents) || 0;
    }
    const shippingRow = drawer?.querySelector('[data-cart-summary-shipping]');
    const shippingText = shippingRow?.textContent?.trim() || '';
    if (!shippingText || /free/i.test(shippingText)) return 0;
    const digits = shippingText.replace(/[^\d.,]/g, '');
    if (!digits) return 0;
    return Math.round(parseFloat(digits.replace(',', '')) * 100) || 0;
  };

  const applySummary = (cart, state, drawer) => {
    const stackRow = drawer.querySelector('[data-cart-stack-discount-row]');
    if (stackRow) {
      stackRow.hidden = true;
      stackRow.setAttribute('hidden', '');
    }
  };

  const updateBundleSkuHint = (cart, drawer) => {
    if (window.GhaliaCartBundleUpgrade?.sync) {
      window.GhaliaCartBundleUpgrade.sync(cart, drawer);
    }
  };

  const loadCart = async (cart, forceFetch = false) => {
    if (!forceFetch) {
      if (cart?.items?.length) return cart;
      if (cart && (cart.item_count === 0 || !cart.items?.length)) return cart;
    }
    const api = window.GhaliaCartApi;
    if (!api?.getCart) return cart?.items ? cart : null;
    try {
      return await api.getCart({ force: forceFetch });
    } catch {
      return cart?.items ? cart : null;
    }
  };

  const cartFingerprint = (cart) => {
    if (!cart?.items?.length) return `empty:${cart?.item_count || 0}`;
    const code = window.GhaliaPromoConfig?.cartDiscountCodeTitle?.(cart) || '';
    return [
      code,
      ...cart.items.map((item) => {
        const alloc = (item.line_level_discount_allocations || []).reduce(
          (sum, a) => sum + (Number(a.amount) || 0),
          0
        );
        return `${item.key}:${item.quantity}:${item.final_line_price || item.line_price || 0}:${alloc}`;
      }),
    ].join('|');
  };

  let lastStackFingerprint = '';
  let pricingApplyTimer = null;
  let pricingApplyInFlight = false;
  let pendingPricingCart = null;

  const apply = async (cart, options = {}) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const data = await loadCart(cart, options.forceFetch);
    if (!data?.items?.length) return;
    const fingerprint = cartFingerprint(data);
    if (!options.forceFetch && fingerprint === lastStackFingerprint) return;
    lastStackFingerprint = fingerprint;
    const state = computeStackState(data, drawer);
    updateBundleSkuHint(data, drawer);
    applyLinePrices(data, state, drawer);
    applySummary(data, state, drawer);
    window.GhaliaCart = window.GhaliaCart || {};
    window.GhaliaCart.lastStackState = state;
    const drawerEl = document.querySelector('[data-cart-drawer]');
    if (drawerEl && window.GhaliaCartSummary?.apply) {
      window.GhaliaCartSummary.apply(data, state, drawerEl);
    }
    document.dispatchEvent(new CustomEvent('ghalia:cart:stack-discount', { detail: { cart: data, state } }));
  };

  const scheduleApply = (cart, options = {}) => {
    if (cart) pendingPricingCart = cart;
    if (pricingApplyTimer) cancelAnimationFrame(pricingApplyTimer);
    pricingApplyTimer = requestAnimationFrame(() => {
      pricingApplyTimer = null;
      if (pricingApplyInFlight) return;
      const nextCart = pendingPricingCart;
      pendingPricingCart = null;
      if (!nextCart) return;
      pricingApplyInFlight = true;
      apply(nextCart, options).finally(() => {
        pricingApplyInFlight = false;
        if (pendingPricingCart) scheduleApply(pendingPricingCart, options);
      });
    });
  };

  const resetFingerprint = () => {
    lastStackFingerprint = '';
  };

  const repaintCart = (cart, options = {}) => {
    if (!cart?.items?.length) return;
    if (options.resetFingerprint) resetFingerprint();
    scheduleApply(cart, options);
  };

  window.GhaliaCartStackDiscount = { resetFingerprint, repaintCart };

  const bind = () => {
    document.addEventListener('ghalia:cart:pricing', (e) => {
      const cart = e.detail?.cart;
      if (!cart?.items?.length) return;
      scheduleApply(cart);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
